<template>
  <div
    class="form-autocomplete"
    :class="{
      isOpen: showSuggestions,
      'has-error': error,
      'has-gray-background': hasGrayBackground,
    }"
    v-closable="closeSuggestions"
  >
    <input
      class="form-autocomplete__input"
      type="text"
      v-model="query"
      @keydown.enter="enterKey"
      @keydown.down="downKey"
      @keydown.up="upKey"
      @keydown.tab="closeSuggestions"
      @keyup.esc="closeSuggestions"
      @focus="openSuggestions"
      @click="openSuggestions"
      @input="queryEdited"
      :placeholder="placeholder"
      :aria-label="placeholder"
    />

    <BaseIcon class="form-autocomplete__search-icon" name="search" />

    <div v-show="isLoading" class="form-autocomplete__loading-bar">
      <BaseLoadingBar is-small />
    </div>

    <span v-if="error" class="form-autocomplete__error-msg">{{ error }}</span>

    <simplebar
      class="form-autocomplete__menu"
      :class="{ 'form-autocomplete__menu--overlay': overlayMenu }"
      v-if="showSuggestions"
      :style="{ maxHeight: normalizedMenuMaxHeight }"
    >
      <ul class="form-autocomplete__list">
        <li
          v-for="(suggestion, index) in matches"
          :key="index"
          class="form-autocomplete__item"
          @click="selectSuggestion(index)"
        >
          <span class="form-autocomplete__option" v-bind:class="{ selected: isSelectedItem(suggestion) }">{{
            getSuggestionText(suggestion)
          }}</span>
        </li>
      </ul>
    </simplebar>

    <div class="form-autocomplete__no-results" v-if="showNoResults">{{ $t('form-auto-complete.no-results') }}</div>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import DebounceMixin from '@/mixins/debounce-mixin';
import simplebar from 'simplebar-vue';

export default {
  name: 'FormAutocomplete',
  components: {
    BaseLoadingBar,
    BaseIcon,
    simplebar,
  },
  mixins: [DebounceMixin],
  props: {
    // This component can be used without model and use the emitted 'query-edited' event only
    value: {
      type: [Object, String, Number],
      required: false,
    },
    // The suggestions list to display (can be client-side static or provided and updated by a server)
    suggestions: {
      type: Array,
      required: true,
      default: () => [],
    },
    // If using objects, this field is used to determine what is the ID property of the object (for comparison purposes)
    idProp: {
      type: String,
      required: false,
    },
    // If using objects, this field is used to determine what is the label property of the object (used for displaying)
    labelProp: {
      type: String,
      required: false,
    },
    // If using objects, this field is used to determine what is the value property of the object (when selecting a suggestion)
    valueProp: {
      type: String,
      required: false,
    },
    // If true, the component will try to filter the suggestions list innerly (mainly used for client-side static lists).
    // Set to false if the suggestions are updated and filtered server-side.
    filterByQuery: {
      type: Boolean,
      required: false,
    },
    hasGrayBackground: {
      type: Boolean,
      required: false,
    },
    // If true, displays a loading bar
    isLoading: {
      type: Boolean,
      required: false,
    },
    // If true, clears the query on item select
    clearOnSelect: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    error: {
      type: String,
      default: null,
    },
    menuMaxHeight: {
      type: [Number, String], // e.g., 280 or "40vh" or "320px"
      default: 280,
    },
    // If true, the suggestion list floats over whatever sits below the input instead of taking room
    // in the layout (the way the "no results" box already does). Off by default: existing call sites
    // live in popovers that are sized to grow with the list, and clipping them would be worse.
    overlayMenu: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      query: '',
      isOpen: false,
      currentIdx: 0,
      // A null value that arrived while the list was open, applied when it closes.
      clearOnClose: false,
    };
  },
  computed: {
    matches() {
      if (!this.suggestions) return [];
      if (!this.filterByQuery) return this.suggestions;
      return this.suggestions.filter((s) => {
        return this.getSuggestionText(s).toLowerCase().includes(this.query.toLowerCase());
      });
    },
    showSuggestions() {
      return this.isOpen && this.matches.length > 0;
    },
    showNoResults() {
      return !this.isLoading && this.isOpen && this.matches.length === 0 && !!this.query;
    },
    normalizedMenuMaxHeight() {
      const v = this.menuMaxHeight;
      return typeof v === 'number' ? `${v}px` : v; // keep provided units if string
    },
  },
  mounted() {
    this.syncQuery();
  },
  watch: {
    value(next) {
      // Contract: a parent clearing the selection (null) while the user is mid-search — the list is
      // open — must not erase what they typed. Clearing means "drop the stale choice", not "drop the
      // search". Once the list closes (select, Tab, Esc, click-away) a null value clears the field
      // as it always did, so a consumer that clears on close sees no difference.
      if (next == null && this.isOpen) {
        this.clearOnClose = true;
        return;
      }
      this.clearOnClose = false;
      this.syncQuery();
    },
  },
  methods: {
    upKey() {
      if (this.currentIdx > 0) this.currentIdx--;
    },
    downKey() {
      if (this.currentIdx < this.suggestions.length - 1) this.currentIdx++;
    },
    enterKey() {
      this.selectSuggestion(this.currentIdx);
    },
    isActiveItem(index) {
      return index === this.currentIdx;
    },
    isSelectedItem(suggestion) {
      if (!this.value) return false;
      return this.getSuggestionId(suggestion) === this.getSuggestionId(this.value);
    },
    queryEdited() {
      this.debounce(() => {
        this.isOpen = true;
        this.currentIdx = 0;
        this.$emit('query-edited', this.query);
      });
    },
    openSuggestions() {
      this.isOpen = true;
    },
    closeSuggestions() {
      this.isOpen = false;
      if (this.clearOnClose) {
        this.clearOnClose = false;
        this.syncQuery();
      }
    },
    selectSuggestion(index) {
      if (index > this.matches.length - 1) return;
      let suggestion = this.matches[index];
      this.query = this.clearOnSelect ? '' : this.getSuggestionText(suggestion) || '';
      this.$emit('input', this.getSuggestionValue(suggestion));
      this.clearOnClose = false;
      this.closeSuggestions();
    },
    syncQuery() {
      this.query = this.getSuggestionText(this.value) || '';
    },
    getSuggestionText(suggestion) {
      if (suggestion && this.labelProp) {
        let label = this.resolvePath(suggestion, this.labelProp);
        if (label) return label;
      }
      return suggestion;
    },
    getSuggestionId(suggestion) {
      if (suggestion && this.idProp) {
        let id = this.resolvePath(suggestion, this.idProp);
        if (id) return id;
      }
      let value = this.getSuggestionValue(suggestion);
      if (value) return value;
      return suggestion;
    },
    getSuggestionValue(suggestion) {
      if (suggestion && this.valueProp) {
        let value = this.resolvePath(suggestion, this.valueProp);
        if (value) return value;
      }
      return suggestion;
    },
    resolvePath(obj, path) {
      if (!obj || !path) return null;
      return path.split('.').reduce((o, i) => o[i], obj);
    },
  },
};
</script>

<style lang="scss">
@import '~simplebar/dist/simplebar.css';

.form-autocomplete {
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
  width: 100%;
  overflow: visible; /* was hidden -> causes clipping/double scroll */

  &__input {
    background-color: $color-white;
    border: 1px solid $color-neutral-pale;
    border-radius: $border-radius-small;
    color: $color-normal;
    font-size: 14px;
    min-height: 40px;
    outline: none;
    padding: 0 27px 0 8px;
    transition: background 200ms ease, border 200ms ease;
    width: 100%;

    &:hover,
    &:active {
      border-color: $color-normal-pale;
    }

    &:focus {
      border-color: $color-accent;
    }
  }

  &__search-icon {
    position: absolute;
    color: $color-neutral;
    height: 16px;
    top: 12px;
    right: 8px;
    width: 16px;
  }

  &.has-error {
    .form-autocomplete__input {
      border-color: $color-error;
    }
  }

  &.has-gray-background {
    .form-autocomplete__input {
      background-color: $color-neutral-pale;
    }
    .form-autocomplete__search-icon {
      color: $color-normal-pale;
    }
  }

  &__loading-bar {
    margin-top: 1px;
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
  }

  &__error-msg {
    bottom: -20px;
    color: $color-error;
    font-size: 12px;
    font-style: italic;
    position: absolute;
    right: 0;
  }

  &__no-results {
    background-color: $color-white;
    border-radius: $border-radius-small;
    box-shadow: $shadow-dropdown;
    color: $color-error;
    font-size: 14px;
    left: 0;
    margin: 6px 0 0;
    min-width: 160px;
    padding: 10px 16px;
    position: absolute;
    top: 40px;
    width: 100%;
    z-index: $third-index;
  }

  &__menu {
    background-color: $color-white;
    border-radius: $border-radius-small;
    box-shadow: $shadow-dropdown;
    overflow-x: hidden;
    z-index: $third-index;

    &--overlay {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
    }
  }

  &__list {
    font-size: 14px;
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: left;
  }

  &__item {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  &__option {
    color: $color-normal;
    cursor: pointer;
    display: block;
    padding: 8px 16px;
    text-decoration: none;

    &.selected {
      color: $color-accent;
    }

    &:hover {
      color: $color-neutral;
    }
  }
}
</style>
